import { useId, useState, type ReactNode } from "react";
import {
  REDIRECT_URI_BOX, SIGN_IN_APP_NOT_KNOWN_STEP, appIdProblem, normaliseAppId, ownAppCopy, ownAppRegisterLine, ownAppSteps,
} from "../lib/eam";
import type { EamPhoneSource } from "../types/api";
import CopyButton from "./CopyButton";
import RichText from "./RichText";

const BUTTON =
  "inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-subtle text-sm font-medium text-white bg-accent hover:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

interface Props {
  /** Missing on an older backend: the redirect row gives way to "not known". */
  redirectUri: string | undefined;
  tenantId: string;
  /** The realm's number source: on an Entra source Mideye's app still reads phone numbers. */
  phoneSource: EamPhoneSource | null;
  /** Saves the ID; resolves to the backend's message for the field, or null. */
  onSave: (appId: string) => Promise<string | null>;
  busy: boolean;
  /** False: the instructions alone, for "Show the steps" on a saved ID. */
  showField?: boolean;
  /** The read-access tag, shown directly under the register line. */
  readStatus?: ReactNode;
  /** Shown just before Save, in its row (the sign-in app panel's Close). */
  besideSave?: ReactNode;
}

export default function OwnAppSteps({
  redirectUri, tenantId, phoneSource, onSave, busy, showField = true, readStatus, besideSave,
}: Props) {
  const [value, setValue] = useState("");
  const [touched, setTouched] = useState(false);
  const [serverProblem, setServerProblem] = useState<string | null>(null);
  const errorId = useId();
  const normalised = normaliseAppId(value);
  const problem = value.trim() === "" ? null : appIdProblem(normalised, tenantId);
  const shown = (touched ? problem : null) ?? serverProblem;
  const registerLine = ownAppRegisterLine(phoneSource);

  return (
    <div>
      {registerLine && <p className="text-sm text-gray-500">{registerLine}</p>}
      {readStatus}
      <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-gray-500">
        {ownAppSteps(showField).map((step, i) => (
          <li key={i}>
            <RichText parts={step.title} />
            {step.items.length > 0 && (
              <ul className="mt-1 list-disc space-y-1 pl-5">
                {step.items.map((item, j) =>
                  item === REDIRECT_URI_BOX ? (
                    <li key={j} className="list-none">
                      {redirectUri ? (
                        <div className="flex items-center gap-3 rounded-md bg-gray-50 px-3 py-2">
                          <span className="min-w-0 flex-1 break-all font-mono text-gray-800">{redirectUri}</span>
                          <CopyButton text={redirectUri} title={ownAppCopy.copyRedirect} />
                        </div>
                      ) : (
                        <div className="rounded-md bg-gray-50 px-3 py-2 text-gray-800">{SIGN_IN_APP_NOT_KNOWN_STEP}</div>
                      )}
                    </li>
                  ) : (
                    <li key={j}>
                      <RichText parts={item} />
                    </li>
                  ),
                )}
              </ul>
            )}
          </li>
        ))}
      </ol>
      {showField && (
        <form
          className="mt-4"
          onSubmit={(e) => {
            e.preventDefault();
            setTouched(true);
            if (problem || normalised === "") return;
            void onSave(normalised).then(setServerProblem);
          }}
        >
          <label htmlFor="ownAppId" className="block text-sm font-medium text-gray-700">
            {ownAppCopy.fieldLabel}
          </label>
          <div className="mt-1 flex flex-wrap items-start gap-3">
            <input
              id="ownAppId"
              className="input min-w-0 flex-1 font-mono"
              placeholder={ownAppCopy.placeholder}
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setServerProblem(null);
              }}
              onBlur={() => setTouched(true)}
              aria-invalid={shown ? true : undefined}
              aria-describedby={shown ? errorId : undefined}
              autoComplete="off"
              spellCheck={false}
            />
            {besideSave}
            <button type="submit" className={BUTTON} disabled={busy || normalised === ""}>
              {busy ? ownAppCopy.saving : ownAppCopy.save}
            </button>
          </div>
          {shown && (
            <p id={errorId} role="alert" className="mt-1 text-xs text-red-600">
              {shown}
            </p>
          )}
        </form>
      )}
    </div>
  );
}
